import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { licenseService } from '../services/licenseService';
import { authService } from '../services/authService';
import './LicenseList.css';

function LicenseList() {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();

  const loadLicenses = async () => {
    try {
      setLoading(true);
      setError(''); // Limpiar error anterior
      
      // Verificar que tenemos token antes de hacer la petición
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No se encontró token de autenticación');
        navigate('/login');
        return;
      }
      
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      
      const response = await licenseService.getAll(params);
      setLicenses(response.data.licenses);
      setRetryCount(0); // Reset retry count on success
    } catch (err) {
      console.error('Error al cargar licencias:', err);
      const errorMsg = err.response?.data?.message || 
                       err.response?.data?.error || 
                       err.message || 
                       'Error al cargar licencias';
      
      // Si es error de red y es el primer intento, reintentar automáticamente
      if ((err.message === 'Network Error' || err.code === 'ERR_NETWORK') && retryCount < 2) {
        console.log(`Reintentando carga... (intento ${retryCount + 1})`);
        setRetryCount(prev => prev + 1);
        // Reintentar después de un breve delay
        setTimeout(() => {
          loadLicenses();
        }, 500);
        return;
      }
      
      setError(errorMsg);
      setLicenses([]); // Limpiar lista en caso de error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLicenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta licencia?')) {
      return;
    }

    try {
      const response = await licenseService.delete(id);
      if (response.success) {
        loadLicenses();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 
                       err.response?.data?.error || 
                       err.message || 
                       'Error al eliminar licencia';
      alert(errorMsg);
      console.error('Error al eliminar:', err.response?.data || err);
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const formatDate = (date) => {
    if (!date) return 'Sin expiración';
    return new Date(date).toLocaleDateString('es-ES');
  };

  const getStatusBadge = (license) => {
    if (license.status === 'inactive') {
      return <span className="badge badge-inactive">Inactiva</span>;
    }
    
    if (license.expiresAt && new Date(license.expiresAt) < new Date()) {
      return <span className="badge badge-expired">Expirada</span>;
    }
    
    return <span className="badge badge-active">Activa</span>;
  };

  return (
    <div className="license-list-container">
      <header className="header">
        <h1>Gestión de Licencias</h1>
        <button onClick={handleLogout} className="btn btn-logout">
          Cerrar Sesión
        </button>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="controls">
        <div className="filters">
          <input
            type="text"
            placeholder="Buscar por clave, nombre o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">Todos los estados</option>
            <option value="active">Activas</option>
            <option value="inactive">Inactivas</option>
          </select>
        </div>

        <button
          onClick={() => navigate('/licenses/new')}
          className="btn btn-primary"
        >
          + Nueva Licencia
        </button>
      </div>

      {loading ? (
        <div className="loading">Cargando licencias...</div>
      ) : licenses.length === 0 ? (
        <div className="empty-state">
          <p>No hay licencias para mostrar</p>
          <button onClick={() => navigate('/licenses/new')} className="btn btn-primary">
            Crear primera licencia
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="license-table">
            <thead>
              <tr>
                <th>Clave de Licencia</th>
                <th>Producto</th>
                <th>Cliente</th>
                <th>Estado</th>
                <th>Expira</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {licenses.map((license) => (
                <tr key={license.id}>
                  <td className="license-key">{license.licenseKey}</td>
                  <td className="product-name">{license.product}</td>
                  <td>
                    <div className="customer-info">
                      <div className="customer-name">{license.customerName || 'N/A'}</div>
                      <div className="customer-email">{license.customerEmail || ''}</div>
                    </div>
                  </td>
                  <td>{getStatusBadge(license)}</td>
                  <td>{formatDate(license.expiresAt)}</td>
                  <td>
                    <div className="actions">
                      <button
                        onClick={() => navigate(`/licenses/${license.id}`)}
                        className="btn btn-sm btn-edit"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(license.id)}
                        className="btn btn-sm btn-delete"
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default LicenseList;
